/**
 * Global client state — spec §F1 ("Global state (Zustand store lib/state.ts) — port the
 * mockup's `state` object"). The `AppState` interface below is the spec's, verbatim;
 * `AppActions` carries the M1 setters. Server state lives in TanStack Query (lib/api.ts),
 * never here.
 */
import { create } from 'zustand';

import { ringId } from './aoi';

/** §I1 roles, lowest privilege first. */
export type Role = 'public' | 'field' | 'partner' | 'analyst' | 'manager';

export type Country = 'ALL' | 'BFA' | 'MLI' | 'NER' | 'NGA';

export type Basemap = 'light' | 'streets' | 'satellite' | 'terrain';

/** The overlay-hierarchy reporting modes (§M3.5) — levels that are not admin levels. */
export const WATERSHED_LEVEL = 'watershed';
export const PARTNERSHIP_LEVEL = 'partnership';

/**
 * The two overlay hierarchies, as reporting modes.
 *
 * They are not interchangeable, and the difference is the whole reason `watershedReady`
 * exists: MELS reports B.2.1/C.2.1 **natively** per watershed, so that radio is live only
 * for an indicator that has such values; nothing reports natively per partnership, so §E4's
 * area-weighted roll-up is the only answer there ever is and the radio is live for every
 * indicator that has commune values at all.
 */
export const OVERLAY_LEVELS = [WATERSHED_LEVEL, PARTNERSHIP_LEVEL] as const;

export function isOverlayLevel(level: unknown): level is OverlayLevel {
  return level === WATERSHED_LEVEL || level === PARTNERSHIP_LEVEL;
}

/**
 * §F1 `reportingLevel`, widened by spec §M3.5.
 *
 * §F1 wrote this as `0 | 1 | 2` because the watershed radio was disabled: watershed values
 * were to be area-weighted from admin cells later. Part M changed that — MELS serves B.2.1
 * and C.2.1 **per `watershed_id`**, so a watershed has its own reported number and the
 * radio becomes meaningful for those indicators ("the Operational-hierarchy watershed radio
 * can be enabled for MELS-backed indicators … as soon as M3 lands"). `'watershed'` is
 * therefore a fourth reporting mode rather than a fourth admin level: the choropleth draws
 * `type=watershed` polygons and asks `/values?level=watershed` (docs/NOTES.md).
 */
export type OverlayLevel = (typeof OVERLAY_LEVELS)[number];
export type ReportingLevel = 0 | 1 | 2 | OverlayLevel;

/** The admin level to fall back on where only `0|1|2` makes sense (bulk rows, labels). */
export function adminLevelOf(level: ReportingLevel, fallback: 0 | 1 | 2 = 1): 0 | 1 | 2 {
  return isOverlayLevel(level) ? fallback : level;
}

/**
 * The i18n key naming a reporting level — one table, because the legend footer (§F5.6),
 * the choropleth tooltip (§F5.3) and the indicator pane's "mapped at …" line all print it
 * and disagreeing would mean the map and its legend describe different geographies.
 */
const LEVEL_LABEL_KEYS: Record<string, string> = {
  0: 'indicators.levelCountry',
  1: 'indicators.levelRegion',
  2: 'indicators.levelCommune',
  [WATERSHED_LEVEL]: 'indicators.levelWatershed',
  [PARTNERSHIP_LEVEL]: 'indicators.levelPartnership',
};

export function levelLabelKey(level: ReportingLevel | number | null | undefined): string {
  return LEVEL_LABEL_KEYS[String(level)] ?? 'indicators.levelRegion';
}

/* ---------------------------------------------- §F5.8 scope (the cascading filter) */

/**
 * The unit a reader has filtered the whole Map Explorer down to.
 *
 * §F1's country selector narrows the map to one of four countries; the client asked for the
 * same thing one, two and three levels further down — "pick a region and everything follows,
 * the numbers included". A scope is that pick: one `org_units` row, and the map draws its
 * subtree and nothing else (`?parent=` on §D2/§D3 — see `apps/hierarchy/subtree.py`).
 *
 * `country` and `name` are carried rather than looked up because both are needed *without* a
 * round trip: `country` so the global selector and the scope can never contradict each other
 * (see `setScope`/`setCountry`), `name` so the §F5.10 status crumb and the widget hint can
 * say where the reader is the moment they pick it. The permalink stores only `unitId` and
 * `app/PermalinkSync` rebuilds the rest from `/hierarchy/units/{id}` — a scope is a fact
 * about the server's hierarchy, and a hash is not allowed to assert one.
 *
 * A **country** is deliberately not a scope: §F1 already has a global control for that, two
 * filters meaning the same thing would be two ways to disagree, and picking a country in the
 * cascade therefore writes `country`. The type still admits `adminLevel: 0` because a
 * hand-edited `sc=BF` must resolve to something rather than crash the boot.
 */
export interface Scope {
  unitId: string;
  unitType: 'admin' | 'watershed' | 'partnership';
  /** `0|1|2` for an admin unit, `null` for a watershed or a partnership. */
  adminLevel: 0 | 1 | 2 | null;
  /**
   * The unit above this one (§D2 `parent_id`) — how the cascade's *upper* select knows what
   * to show. A commune scope has to leave "Djoro" standing in the Region box, and the only
   * other way to find that out would be to search the region list for a parent match.
   */
  parentId: string | null;
  /** ISO3 of the unit, or `null` for a cross-border watershed (§C). */
  country: string | null;
  /** The unit's own name — the §F5.10 crumb, without a fetch. */
  name: string;
}

/**
 * A §D2 unit row — from `/hierarchy/units/{id}` or `/hierarchy/options` — as a `Scope`.
 *
 * Structural rather than typed against `lib/api.ts` because that module imports *this* one;
 * the shared shape is the point, so a permalink restore and a dropdown pick can never build
 * two different scopes for the same unit.
 */
export function scopeFromUnit(unit: {
  id: string;
  name: string;
  unit_type: string;
  admin_level: number | null;
  parent_id: string | null;
  country: string | null;
}): Scope {
  return {
    unitId: unit.id,
    unitType: unit.unit_type as Scope['unitType'],
    adminLevel: unit.admin_level as Scope['adminLevel'],
    parentId: unit.parent_id,
    country: unit.country,
    name: unit.name,
  };
}

/** Every reporting level, in §F5.8 row order — what "no filter" allows. */
export const ALL_REPORTING_LEVELS: readonly ReportingLevel[] = [
  0, 1, 2, WATERSHED_LEVEL, PARTNERSHIP_LEVEL,
];

/**
 * The level an **unfiltered** map reports at — client review R5.
 *
 * R5 made the reporting level a function of the cascade rather than a control of its own
 * ("reporting level derives from the cascade: none→regions; country→its regions; region→its
 * communes; watershed→watershed/partnerships"). The five §F5.8 radios are gone, so this is
 * the level every path back out of a filter lands on: `clearScope`, and the country change
 * that drops a scope belonging to somewhere else.
 *
 * It is deliberately *not* `lastAdminLevel` any more. That field existed to remember which
 * radio the reader had pressed before a filter borrowed the level; with no radios there is
 * nothing to remember, and "the level you were at before you filtered" and "the level the
 * unfiltered map opens at" are now the same fact. `lastAdminLevel` survives as the §M3.5
 * fallback target for `'watershed'` (a permalink can still name that mode).
 */
export const NO_SCOPE_LEVEL: ReportingLevel = 1;

/**
 * The reporting levels a scope leaves meaningful — the §F5.8 radios that stay live.
 *
 * The rule is "the scope unit, or its children": a region is mapped by painting its
 * communes, a commune by painting itself, a watershed by painting itself **or** its
 * partnerships (the one scope with a genuine choice), a partnership by painting itself.
 * Anything else would ask §D3 for units that are not in the subtree, i.e. for an empty map.
 *
 * A country scope (only reachable from a hand-edited hash — see `Scope`) allows both admin
 * levels below it, and defaults to regions, which is where the unscoped map already opens.
 */
export function scopeLevels(scope: Scope | null): readonly ReportingLevel[] {
  if (!scope) return ALL_REPORTING_LEVELS;
  if (scope.unitType === WATERSHED_LEVEL) return [WATERSHED_LEVEL, PARTNERSHIP_LEVEL];
  if (scope.unitType === PARTNERSHIP_LEVEL) return [PARTNERSHIP_LEVEL];
  return scope.adminLevel === 0 ? [1, 2] : [2];
}

/** The level a scope opens on: the first one it allows. */
export function defaultScopeLevel(scope: Scope): ReportingLevel {
  return scopeLevels(scope)[0] as ReportingLevel;
}

/**
 * The reporting level the scope unit **itself** is served at.
 *
 * `?parent=X` is inclusive, so asking §D3 for X's own level under X's own scope returns
 * exactly one row: X's value. That is how §F5.9's dock popover gets "the number for the
 * thing you filtered to" without a per-unit endpoint — and it is *not* `defaultScopeLevel`,
 * which for a region is the level of its children.
 */
export function scopeOwnLevel(scope: Scope): ReportingLevel {
  if (scope.unitType === WATERSHED_LEVEL) return WATERSHED_LEVEL;
  if (scope.unitType === PARTNERSHIP_LEVEL) return PARTNERSHIP_LEVEL;
  return scope.adminLevel ?? 1;
}

/**
 * `wanted` if the scope allows it, else `fallback`, else the scope's default.
 *
 * Keeping `wanted` is what makes the level *sticky* across a scope change — a reader
 * comparing two watersheds at partnership level stays at partnership level when they pick
 * the second one — and it is also how a permalink's `lv=` survives its own `sc=`.
 */
export function levelWithinScope(
  scope: Scope | null,
  wanted: ReportingLevel,
  fallback: ReportingLevel,
): ReportingLevel {
  const allowed = scopeLevels(scope);
  if (allowed.includes(wanted)) return wanted;
  if (allowed.includes(fallback)) return fallback;
  return allowed[0] as ReportingLevel;
}

/** §D2/§D3 `?parent=` for the current scope — `null` is "the whole programme". */
export const selectScopeParent = (s: Pick<AppState, 'scope'>): string | null =>
  s.scope?.unitId ?? null;

/* ------------------------------------------- client review R4: one raster at a time */

/**
 * Which §D5 catalogue rows reach the map as **tiles** — the register R4's invariant needs.
 *
 * R4: *"no raster-over-raster — while one raster layer is on, other raster rows are disabled
 * … vector layers combine freely"*. The rule has to live in the store, because two separate
 * controls now switch layers on (the §F5.2 Layers pane and R8's layer manager) and a rule
 * implemented in either of them would be a rule the other could break.
 *
 * But `toggleLayer(id)` is given an **id**, and whether that id is a raster is a fact about
 * the §D5 catalogue — server state, which lives in TanStack Query and must not be copied
 * into `AppState` (the §F1 interface is fixed verbatim, `shareHash` round-trips it and
 * `state.test.ts` asserts it field by field). So the catalogue's raster ids are held here, in
 * a module-level register that whoever holds the `['layers', role]` query writes on arrival
 * (`useRasterRegistry`, features/map/layerKinds.ts).
 *
 * An **empty** register means no layer is a raster, so the invariant simply does not bite
 * until the catalogue has answered. That is the right failure: a rule that cannot yet be
 * evaluated must not block a reader, and the pane's own tooltip is derived from the same
 * register, so control and rule can never disagree about which rows are rasters.
 *
 * **Registering is also enforcing** (R6 fix). `toggleLayer` guards the two panes, but it is
 * not the only writer of `layersOn`: `permalink.applyToStore` assigns the whole set through
 * `setState`, and it *must* — it runs before `createRoot`, so `/layers` does not exist yet and
 * the register is still empty, which means `toggleLayer` could not evaluate the rule there
 * either. `#ly=soc,sgphwms` therefore hydrated two rasters, `RasterLayers` stacked them
 * opaquely, and the pane then disabled the control for the one on *top* — the reader could
 * not switch off the layer they were looking at from either pane. So the moment the catalogue
 * makes the rule evaluable, the set is normalised to satisfy it: the invariant holds for every
 * writer, including ones written later, because it is re-established on the arrival of the
 * only fact that can decide it.
 */
let RASTER_IDS: ReadonlySet<string> = new Set<string>();

export function registerRasterLayers(ids: Iterable<string>): void {
  RASTER_IDS = new Set(ids);
  useAppStore.setState((s) => {
    // The **first** raster in `layersOn` survives, which for a permalink is the first one the
    // hash named — the same row `activeRasterOf` reports as active, so the legend, the two
    // panes and the map all name the layer that stayed.
    const kept = activeRasterOf(s.layersOn);
    if (kept === null) return {};
    const next = new Set<string>();
    for (const id of s.layersOn) if (id === kept || !RASTER_IDS.has(id)) next.add(id);
    return next.size === s.layersOn.size ? {} : { layersOn: next };
  });
}

export function isRasterLayerId(id: string): boolean {
  return RASTER_IDS.has(id);
}

/**
 * The one raster currently drawn, or `null` — R4's whole state, derived rather than stored.
 *
 * Derived because a stored copy is a second source of truth for "is this layer on", and the
 * legend's ✕, the pane checkbox, the manager checkbox and a permalink restore all write
 * `layersOn` without going near it. Returns an id (a stable string), so a component
 * subscribing through it re-renders only when the answer actually changes.
 */
export function activeRasterOf(layersOn: ReadonlySet<string>): string | null {
  for (const id of layersOn) if (RASTER_IDS.has(id)) return id;
  return null;
}

export const selectActiveRaster = (s: Pick<AppState, 'layersOn'>): string | null =>
  activeRasterOf(s.layersOn);

/* ------------------------------------- client round 4 E2: one mapping at a time */

/** The §F5.2 pane tabs, named — the two mappings a reader can put on the map. */
export type SidebarTab = 'inds' | 'layers';

/** Anything on the indicator side? §F5.2's fill, §F5.5's overlays, §F5.4's mode. */
export const selectIndicatorMapped = (
  s: Pick<AppState, 'indicator' | 'overlays' | 'biv'>,
): boolean => Boolean(s.indicator) || s.overlays.length > 0 || s.biv.on;

/** Anything on the layer side? The D2 raster pick and every vector added under it. */
export const selectLayersMapped = (s: Pick<AppState, 'layersOn'>): boolean =>
  s.layersOn.size > 0;

/* ------------------------- client round 4 E4: the scope mask, and what it outranks */

/**
 * Is the cascade filter's geography set at all? — a scope, **or** the country rung alone.
 *
 * The country box is the first of the cascade's five rungs (`CascadeFilter`), and picking in
 * it writes `country` and no `scope`: there is no `Scope` for a country, because §D3 asks for
 * a *parent* and the country's children are what the map is already drawing. So a state with
 * `country: 'BFA'` and no scope is a filtered map by every other reading — the choropleth is
 * one country's regions, `clearScope` offers to clear it (D3) — and E4's mask has to agree,
 * which is the round 5 correction: it did not, and a country-level filter left `soc` painted
 * across all four countries.
 */
export function filterGeographySet(s: Pick<AppState, 'scope' | 'country'>): boolean {
  return Boolean(s.scope) || s.country !== 'ALL';
}

/**
 * Is the raster masked to the §F5.8 filter's geography right now?
 *
 * Three facts, and the order they are read in **is** E4's precedence rule (docs/NOTES.md):
 *
 * 1. the filter's geography is set — the mask has an outline to cut to. A scope, or the
 *    cascade's country rung on its own (`filterGeographySet`);
 * 2. the manual AOI clip is **off** — an AOI clip is a reader asking for this exact thing
 *    with a polygon of their own, and one element has one `clip-path`, so the hand-drawn one
 *    wins. Were it the other way round, pressing *Clip* under a filter would do nothing
 *    visible and read as a broken button;
 * 3. a raster is actually drawn — masking an empty pane is a rule with no subject, and it
 *    would take swipe away (below) from a reader who has no raster on.
 *
 * Swipe is the one this outranks: `setSwipe` refuses while it holds, and every write that can
 * make it hold releases swipe, exactly as `setAoiClip` does. Two writers on one pane's
 * `clip-path` is the §G6 problem this repeats the §G6 answer to.
 *
 * "A raster is drawn" is read from the R4 register (`registerRasterLayers`), which is the
 * store's only access to the §D5 catalogue. On a cold load it is empty until `/layers` lands,
 * so the rule simply does not bite yet — the same deliberate failure `toggleLayer`'s R4 guard
 * makes, and the mask itself does not depend on it (`features/map/ScopeClip` holds the
 * catalogue and reads the kinds directly).
 */
export function scopeMaskActive(
  s: Pick<AppState, 'scope' | 'country' | 'aoiClip' | 'layersOn'>,
): boolean {
  return filterGeographySet(s) && !s.aoiClip && activeRasterOf(s.layersOn) !== null;
}

/**
 * Would the mask hold the raster pane if swipe took it? — the question `setSwipe` asks, and
 * deliberately **not** `scopeMaskActive` of the state as it stands.
 *
 * Turning swipe on releases the AOI clip (§G6, `setSwipe` below), and the AOI clip is the one
 * thing holding the mask down. So `scopeMaskActive` read *before* the write answers about a
 * state the write is in the middle of destroying: a reader who filters, clips, then presses
 * Swipe passes a test that is already out of date and lands in exactly the state the
 * precedence exists to forbid — swipe and the mask both writing `rasterPane.clipPath`, the
 * winner decided by whichever map event fires last.
 *
 * So the question is asked of the state the write would produce. `aoiClip: false` is the only
 * field that write changes besides `swipe` itself, which makes this the whole of it.
 */
export function swipeBlockedByScope(
  s: Pick<AppState, 'scope' | 'country' | 'aoiClip' | 'layersOn'>,
): boolean {
  return scopeMaskActive({ ...s, aoiClip: false });
}

/**
 * §D1 `{id,name,role,org,country_scope}` — the wire shape of `/auth/login` and
 * `/auth/me`. Declared here rather than in `lib/api.ts` because `Role` is, and the
 * store consumes it; `lib/api.ts` re-exports it as part of its §D1 surface.
 * `id === null` ⇒ anonymous, and then `role` is `'public'`.
 */
export interface SessionUser {
  id: number | null;
  name: string | null;
  role: Role;
  org: string | null;
  country_scope: string | null;
}

/**
 * The signed-in half of §D1's user object. `role` is not repeated here: it is
 * `AppState.role`, which the §F3 demo switcher can move independently.
 */
export interface SessionIdentity {
  id: number;
  name: string;
  org: string | null;
  country_scope: string | null;
}

/* ----------------------------------------------- M4 §F5.6(e)/§F6 result layers */

/** The four shapes a §H tool's result can take on the map (`serializers.RESULT_LAYER_KINDS`). */
export const RESULT_LAYER_KINDS = ['units', 'choropleth', 'geojson', 'points'] as const;

export type ResultLayerKind = (typeof RESULT_LAYER_KINDS)[number];

/**
 * One entry of a §H tool's `result_layers` — the server's envelope, in server casing.
 *
 * Snake_case keys because that is what the API sends and this object is stored **as it
 * arrived**: the alternative is a camelCase translation layer whose only job is to be
 * updated whenever a tool grows a field, and the field a translation forgets is a layer
 * that silently paints nothing. The store's own bookkeeping (`ResultLayer`) is camelCase,
 * so the two are told apart at a glance.
 *
 * Which keys are populated depends on `kind`:
 * * `units` — `unit_ids` plus `colors` (unit id → the §H1 CMP colour of that row).
 * * `choropleth` — `values` (unit id → number; a unit with no value is **absent**, never
 *   null — see §H3), with `ramp`/`method`/`k` naming the §G classification and
 *   `unit_label` the units of the number.
 * * `geojson` / `points` — `uri`, the §D6 artefact link, plus an optional `color`.
 */
export interface ResultLayerSpec {
  id: string;
  kind: ResultLayerKind;
  label: string;
  unit_ids?: string[];
  colors?: Record<string, string>;
  values?: Record<string, number>;
  ramp?: string;
  method?: 'quantile' | 'equal';
  k?: number;
  uri?: string;
  unit_label?: string;
  color?: string;
}

/**
 * A result layer as the store holds it: the server's spec plus who produced it and when.
 *
 * `jobId` is what the artefact fetch and the §F6 dock join on, and `tool` is what the
 * legend and any future grouping read; both are on the job rather than in the spec, so
 * they are recorded here at the moment the dock registers the layer rather than depended
 * on from a payload that does not carry them.
 */
export interface ResultLayer {
  /** `spec.id` — `{tool}:{job_id}`, so re-registering the same run replaces it in place. */
  id: string;
  jobId: string;
  tool: string;
  label: string;
  spec: ResultLayerSpec;
  createdAt: number;
}

/** What `addResultLayer` needs that the spec does not carry. */
export interface ResultLayerMeta {
  jobId: string;
  tool: string;
  /** Overrides `spec.label` — the dock may have a localised name for the run. */
  label?: string;
}

/**
 * Spec §F1, copied verbatim — **plus one field**, `user`.
 *
 * §F1's state object was written when the SPA had no session (NOTES #133: the role was
 * faked client-side). §D1 returns four more facts about the signed-in user — id, display
 * name, org and country scope — and the §F3 topbar needs the name for the avatar. They
 * are a small, additive superset of §F1: nothing in the spec's own list changes meaning,
 * and `user === null` is exactly the anonymous/`public` case §D1 defines. Logged as
 * NOTES #142.
 */
export interface AppState {
  role: Role;
  user: SessionIdentity | null;
  country: 'ALL' | 'BFA' | 'MLI' | 'NER' | 'NGA';
  /**
   * §F1 superset — the §F5.8 cascading filter (NOTES #389). `null` is the whole programme,
   * which is every state §F1 could describe; a non-null scope narrows *everything* the map
   * explorer draws and counts to one unit's subtree. See `Scope`.
   */
  scope: Scope | null;
  reportingLevel: ReportingLevel; // set ONLY via Operational hierarchy widget (§M3.5)
  /**
   * §F1 superset (NOTES #333). `'watershed'` is a *conditional* reporting mode: §M3.5 makes
   * the radio live only while the mapped indicator has native watershed values, so switching
   * to an indicator that has none must put the map back at an admin level. Which one? The
   * one the reader was last on — going to `1` from a communes view would silently re-scale
   * their map. Never `'watershed'` itself, so the fallback always terminates.
   */
  lastAdminLevel: 0 | 1 | 2;
  indicator: string | null; // base (fill) indicator id
  overlays: { id: string; color?: string }[]; // max 2
  biv: { on: boolean; y: string | null };
  yearIdx: number;
  years: number[];
  /**
   * Whether the year on the slider is the reader's, rather than a default nobody chose.
   *
   * A cold load has no opinion about the year, so it opens on the catalogue's
   * `default_year` for the mapped indicator (§D3 — the latest *substantially reported*
   * year) as soon as `/indicators` answers. That arrival is asynchronous, and it must never
   * yank the slider away from a year the reader has already expressed: a permalink `y=`
   * and any move of the slider, the ticks or the play button set this, and
   * `applyDefaultYear` becomes a no-op from then on.
   */
  yearPinned: boolean;
  /**
   * §F5.10 superset (NOTES #361). How the time bar reads a year: `false` (the default) is
   * the annual series — what happened *in* the selected year — and `true` is the running
   * total to that year, `?mode=cumulative` on §D3's values/series endpoints.
   *
   * It exists because the stored series became annual: MELS' B.2.1 is published as a
   * programme-to-date figure, the sync used to store *that* at every year, and the time bar
   * therefore animated a curve that could only climb. The accumulation is a legitimate and
   * wanted reading — it is the number on MELS' own dashboard — so it is a reader-facing
   * switch rather than a decision baked into the data.
   *
   * A **global** flag, not a per-indicator one: it is a way of reading the map, and it
   * applies to the fill, the overlays, the profile and the dock at once, exactly like
   * `year`. Indicators that do not accumulate (`is_cumulative: false`) ignore it — the
   * checkbox is hidden for them and no `mode=` is sent (`modeFor`, lib/api.ts).
   */
  cumulative: boolean;
  layersOn: Set<string>;
  layerOpacity: Record<string, number>;
  indStyle: { ramp: string | null; method: 'quantile' | 'equal'; k: number; opacity: number };
  legendMode: 'values' | 'classes';
  domFilter: string;
  layerQuery: string;
  basemap: 'light' | 'streets' | 'satellite' | 'terrain';
  /**
   * §F5.10's drawn ring, its §G7 area — and its `lib/aoi.ts` identity (M4).
   *
   * `id` is `ringId(pts)` and is maintained by `setAoi`, so it can never disagree with the
   * ring it names. See `lib/aoi.ts` for why a ring needs one at all.
   */
  aoi: { pts: [number, number][]; areaKm2: number; id: string } | null;
  aoiClip: boolean;
  aoiFocus: string | null;
  /**
   * §F5.6(e)/§F6: the analysis results currently drawn on the map, oldest first.
   *
   * **Unbounded, and deliberately.** Every other collection in this store has a cap
   * (`MAX_OVERLAYS`, one raster) because two of the same thing fight over the same paint.
   * Result layers do not: they are separate answers to separate questions, and a reader
   * comparing four of them is using the tool as intended. The §F5.6 block header carries
   * the count, and its block-level ✕ is how the list gets short again.
   *
   * **Not cleared by the sidebar tab switch, the country or the scope** — see
   * `switchSidebarTab` and `setCountry`.
   */
  results: ResultLayer[];
  swipe: boolean;
  selectedUnit: string | null;
  dockInd: string | null;
  /**
   * §F6: whether the analysis dock is on screen.
   *
   * Store state rather than `MapTools`' own `useState`, because two controls open it —
   * `#btnAnalysis` in §F5.10's third group and the §F3 topbar's "Analysis" item, which is
   * an *action on `/`* rather than a route (`app/nav.ts`) — and a panel with two switches
   * and one of them private is a panel that can disagree with itself.
   *
   * Deliberately **not** in the permalink: a shared link is a view of the map, and the
   * receiving reader has drawn no AOI and picked no units, so a dock restored mid-form
   * would be a form about nothing.
   */
  analysisOpen: boolean;
  splashOpen: boolean;
  /**
   * Client review R1: the sign-in gate, the geoportal's **first** page.
   *
   * Ahead of `splashOpen` in the sequence, not beside it — while this is true the splash
   * does not render and does not fetch, so a session goes gate → splash → app. The fields
   * behind it are inert (permissions come later); dismissing it is the whole of its
   * contract, which is why one boolean is all the state it needs.
   */
  gateOpen: boolean;
  tourStep: number; // -1 = off
  /**
   * §F1 superset (NOTES #185). §F5.2 specifies the sidebar as "332px, collapsible; body
   * class `sb-open` drives dependent widget offsets" but §F1's state object has no field
   * for it (the mockup kept it in the DOM, toggling `#sidebar.collapsed` directly). It is
   * app state — the §F5.10 map tools and the M3 permalink both need to read it — so it
   * lives here. Default `true`: the pane is open on first load, as in the mockup.
   */
  sidebarOpen: boolean;
}

/** Actions implemented in M1. Later milestones add to this interface, not to AppState. */
export interface AppActions {
  setCountry: (country: Country) => void;
  /**
   * §F5.8 cascade: filter the whole map to one unit's subtree.
   *
   * Two invariants it owns, so no caller can break them by calling in the wrong order:
   *
   * * **the global country follows the scope.** A scope inside Burkina Faso *is* a choice of
   *   country, and leaving the topbar on "All countries" while the map shows one region
   *   would put two geography controls on screen disagreeing with each other.
   * * **the reporting level stays inside the scope** (`levelWithinScope`) — a region scope
   *   paints communes, and a level the subtree has no units at would be an empty map with
   *   the radios still claiming otherwise.
   */
  setScope: (scope: Scope) => void;
  /**
   * §F5.8 "Clear filter" — **back to the first operational level** (client correction D3).
   *
   * Not "undo the last dropdown": the whole geography goes back to what a cold load opens
   * on — no scope, regions, *and* `country: 'ALL'`. IFDC's reading is that a reader who
   * clears a filter is starting again, and leaving the country selector on Burkina Faso
   * after "Clear filter" left them looking at a filtered map with nothing in the cascade
   * saying so. The map's own fit follows from the two fields (`GeographyFocus`): scope
   * `null` + country `ALL` is the programme extent.
   */
  clearScope: () => void;
  setReportingLevel: (level: ReportingLevel) => void;
  setBasemap: (basemap: Basemap) => void;
  /**
   * §F3/§I2: the demo switcher's client-only role change. Moves the gating, never the
   * identity — the server still sees whatever session the cookie carries.
   */
  setRole: (role: Role) => void;
  /** §D1 `GET /auth/me` landed: adopt both the identity and the authoritative role. */
  setSession: (user: SessionUser) => void;

  /* -------------------------------------------------- M2 §F5.2/§F5.3 indicator state */

  /**
   * §F5.2: "Item click: single mode → set/unset base indicator (reset indStyle.ramp …)".
   * The ramp reset is the point — a ramp the user picked for a rainfall anomaly must not
   * survive onto a producer count, whose §G2 auto ramp is a completely different family.
   */
  setIndicator: (id: string | null, hasWatershedValues?: boolean) => void;
  /**
   * §M3.5 invariant, for the paths that do not go through `setIndicator`: a permalink
   * hydrated straight into `setState`, or `PermalinkSync` dropping an indicator a stale link
   * named. Demotes `'watershed'` to `lastAdminLevel` when the mapped indicator turns out not
   * to have native watershed values; a no-op at every admin level.
   *
   * Callers must only pass `true` once `/indicators` has actually answered — reconciling
   * against an empty catalogue would demote every restored watershed link on boot.
   *
   * **`indicatorId` is which indicator the answer is about, and it is load-bearing** (R6 fix).
   * Two effects call this off the same `/indicators` landing — `PermalinkSync`, after it has
   * corrected the indicator a stale link named, and `OperationalHierarchy`, from the render
   * that happened *before* that correction. The demotion is one-way (there is no path back up
   * to `'watershed'`), so the effect holding the stale indicator permanently lost a restored
   * `lv=watershed`, depending on which one React flushed first. Naming the indicator makes the
   * call self-invalidating: an answer about an indicator that is no longer mapped is an answer
   * to a question nobody is asking any more, and is dropped.
   */
  reconcileReportingLevel: (indicatorId: string | null, watershedSupported: boolean) => void;
  /** §F5.6 style panel: patch one or more of `{ramp, method, k, opacity}` live. */
  setIndStyle: (patch: Partial<AppState['indStyle']>) => void;
  /**
   * §F5.10 time bar. Clamped to `years`, so a stale index can never blank the map.
   * Also **pins** the year: from here on the reader owns it (see `yearPinned`).
   */
  setYearIdx: (idx: number) => void;
  /**
   * Open on the catalogue's `default_year` for the mapped indicator — once, and only while
   * nothing has pinned the year (`yearPinned`). `null` — an indicator with no values at
   * all — pins nothing and leaves the slider where it is, so a later indicator with a real
   * default still gets its turn.
   */
  applyDefaultYear: (year: number | null | undefined) => void;
  /**
   * Move off a year the indicator has no data for — the year counterpart of
   * `reconcileReportingLevel`. `preferred` (the catalogue's `default_year`) wins when it is
   * one of the available years; otherwise the newest available one does. An empty list is a
   * no-op: "the catalogue has not answered yet" must not be read as "this indicator has no
   * data".
   */
  snapYearToData: (available: number[], preferred?: number | null) => void;
  /**
   * §F5.10's "Cumulative" checkbox. Purely a *reading* of the same stored data — nothing
   * about the map's geography, indicator or year changes, so nothing else is reset.
   */
  setCumulative: (on: boolean) => void;
  /** §F5.6 legend `Values | Low – High` switch — labelling only, never the paint. */
  setLegendMode: (mode: AppState['legendMode']) => void;
  /** §F5.2 domain chip grid — filters the indicator list only, never the map. */
  setDomFilter: (domain: string) => void;
  /** §F5.3 click → §F5.7 unit profile. */
  setSelectedUnit: (id: string | null) => void;
  /** §F5.2 sidebar collapse; also drives the `sb-open` body class. */
  setSidebarOpen: (open: boolean) => void;
  /**
   * **Client round 4 E2**: moving between the two pane tabs clears the other side.
   *
   * > *"Switching to Layers clears the mapped indicator (fill, overlays, bivariate).
   * > Switching to Indicators clears active layers (the raster pick and vector overlays)."*
   *
   * The two panes had become two ways to paint the same map at once, and the reader who went
   * to switch a layer on came back to a choropleth they had stopped looking at under a raster
   * they could not see through. IFDC's resolution is that a tab is a *mode*: what the map
   * shows is what the open pane is about.
   *
   * **One action rather than a clearance per consumer**, because the clearance has five
   * audiences — the legend blocks, the sidebar analytics, the permalink, the status bar and
   * the map itself — and every one of them already reads `indicator`/`overlays`/`biv`/
   * `layersOn`. Writing those fields once is what makes all five agree without any of them
   * knowing a tab exists.
   *
   * Silent, like every other clearance in this file (`setMapMode`, `addOverlay`'s cap): the
   * *message* wants `t()`, so `Sidebar` raises it and only for the first switch in each
   * direction (`features/sidebar/paneSwitch.ts`).
   *
   * **Interactive switches only.** A permalink restores both sides as they were saved — a
   * shared link is a record of a map, not a click — so `applyToStore` and `PermalinkSync` do
   * not go through here and a link naming `ind=` and `ly=` still opens with both. It is the
   * tab that is exclusive, not the state.
   *
   * **`results` are not part of the mode and are never cleared here (M4).** The tab decides
   * what the *explorer* paints — an indicator or a thematic layer — and an analysis result
   * is neither: it is an answer the reader asked for and waited for, registered in legend
   * block (e) with its own ✕. Throwing one away because they went to the Layers pane to see
   * what the result sits on would be the worst clearance in this file. `state.test.ts`
   * asserts it, so a future clearance cannot slip in with a new field.
   *
   * The §M3.5 demotion `setIndicator(null)` performs is deliberately **not** repeated here:
   * since R5 the reporting level is a consequence of the cascade, and with nothing mapped
   * nothing asks §D3 for values at all. The next `setIndicator` reconciles the level against
   * the indicator that is actually mapped, which is the only moment the answer can be right.
   */
  switchSidebarTab: (tab: SidebarTab) => void;

  /* ------------------------------------------------------------ M7 §F4 splash */

  /**
   * Open or close the §F4 splash overlay. **Closing marks the session**, so the reader is
   * not shown the landing page again on the next route change or reload — the "Overview"
   * nav button is how they ask for it back. Reopening deliberately does *not* unmark it:
   * asking to see the page again is not asking to be shown it again unprompted.
   */
  setSplashOpen: (open: boolean) => void;

  /**
   * Open or close the §F6 analysis dock. Opening also drops the selected unit, because
   * the §F5.7 profile and the dock occupy the same right-hand column (see `analysisOpen`).
   */
  setAnalysisOpen: (open: boolean) => void;

  /* ---------------------------------------------------- client review R1 sign-in gate */

  /**
   * Close (or reopen) the R1 sign-in gate. **Closing marks the session**, exactly like the
   * splash — both of the gate's buttons call this with `false` and nothing else, because
   * both of them mean the same thing to the app today: the reader is in.
   */
  setGateOpen: (open: boolean) => void;

  /* --------------------------------------------------- M3 §F5.2 thematic layers */

  /**
   * §F5.2 Layers pane checkbox **and** §F5.6 block (d)'s ✕ — one action, so the two
   * controls can never disagree about what is drawn. `on` omitted = flip.
   *
   * `layersOn` is a `Set`, and zustand compares by reference: mutating it in place would
   * leave every subscriber unnotified, so a new Set is always constructed.
   *
   * **Client review R4 lives here: at most one raster is ever on.** Switching a second one
   * on is refused — *silently*, for the same reason `addOverlay`'s cap is: R4 asks for a
   * tooltip and a message, and a store action has no `t()`. The callers read
   * `selectActiveRaster` first, disable the row and explain; this is the guarantee for every
   * caller that switches layers on **one at a time**. It is not, on its own, the guarantee for
   * the whole invariant: a permalink assigns `layersOn` wholesale before the catalogue that
   * says which ids are rasters even exists. That half is `registerRasterLayers`, which
   * normalises the set the moment the rule becomes evaluable. Between them there is no writer
   * — a legend block, a permalink, a future tool — that can leave two tile layers stacked
   * opaquely on top of each other. Vectors are untouched and combine freely.
   */
  toggleLayer: (id: string, on?: boolean) => void;
  /**
   * §F5.2 *Raster layer* `<select>` — client correction D2's "one visible raster at a time,
   * **the single pick IS the rule**". `null` is the select's *None* option.
   *
   * The difference from `toggleLayer` is the whole point of the control: a checkbox that
   * refused the second raster was the only honest shape for a list of tick boxes, but a
   * listbox has exactly one value, so picking a second raster **replaces** the first rather
   * than being turned down. Every raster leaves `layersOn` and the pick (if any) goes in, in
   * one write — so there is no intermediate state in which two are on and no order of two
   * `toggleLayer` calls to get wrong. Vectors in the set are untouched.
   *
   * `rasterIds` is *which ids are rasters*, and the caller may pass it because the caller is
   * the one holding the §D5 catalogue. It defaults to the register (`registerRasterLayers`),
   * which is the same list arriving by a different route — but the register is written by
   * whoever holds the **map's** copy of that query, and a pane that could only clear rasters
   * once some other component had mounted would have a *None* option that silently did
   * nothing. A control must not depend on a component it does not contain.
   */
  setActiveRaster: (id: string | null, rasterIds?: Iterable<string>) => void;
  /** §F5.2 per-layer opacity slider, live (0–1). */
  setLayerOpacity: (id: string, opacity: number) => void;
  /** §F5.2 *Filter layers* input — narrows the tree only, never the map. */
  setLayerQuery: (query: string) => void;

  /* ------------------------------------------------ M3 §F5.5 overlay indicators */

  /**
   * §F5.2's `+` button. Appends `{id}` (no colour: §G4 picks one against the fill ramp).
   *
   * **Capped at `MAX_OVERLAYS`, silently.** §F5.5's "Max 2 overlays (toast)" needs a
   * *message*, and a store action is the wrong place to raise one — it has no `t()` and no
   * view. So the cap lives here as the guarantee that state can never hold three, and the
   * caller checks `overlays.length` first to decide whether to toast. Re-adding an id
   * already on the list is a no-op rather than a duplicate, which is what makes the button
   * idempotent under a double click.
   */
  addOverlay: (id: string) => void;
  /** §F5.2 `+`-again and §F5.6(c) ✕ — one action, so the two controls stay in sync. */
  removeOverlay: (id: string) => void;
  /**
   * §F5.6(c) "recolour:" swatches. `undefined` **clears** the override and hands the
   * overlay back to §G4's auto-contrast pick — which is what clicking the already-selected
   * swatch does.
   */
  setOverlayColor: (id: string, color: string | undefined) => void;
  /** §F5.4: "On enter: clear overlays (toast why)". Also §F5.11's mutual exclusion. */
  clearOverlays: () => void;

  /* ---------------------------------------------------- M3 §F5.4 bivariate mode */

  /**
   * §F5.2's *Map mode* segment. Entering bivariate **keeps the base indicator as X** and
   * empties `overlays` — §F5.11's "bivariate excludes overlays and vice versa" — and
   * leaving it drops the Y pick, so the two modes can never share a half-state.
   *
   * The overlay clearance is silent here for the same reason `addOverlay`'s cap is (see
   * above): §F5.4 wants a toast *explaining why*, and a store action has no `t()`. The
   * caller reads `overlays.length` before switching and raises the message.
   */
  setMapMode: (mode: MapMode) => void;
  /**
   * §F5.4's Y axis — the second indicator, picked by clicking a row while bivariate is on.
   * A no-op outside bivariate mode: `biv.y` without `biv.on` is a state the renderer, the
   * legend and the sidebar highlight all read as "no bivariate", and letting it be written
   * would leave a stale Y waiting to reappear the next time the mode is switched on.
   */
  setBivY: (id: string | null) => void;

  /* -------------------------------------------------- M3 §F5.9 indicator dock */

  /**
   * §F5.9: "Click toggles popover". One action for both the dock button and the popover's
   * ✕ — clicking the *active* button closes the panel it opened, which is what makes the
   * dock a set of toggles rather than six ways to open the same widget.
   *
   * Deliberately **independent of `indicator`**: the popover describes an indicator without
   * mapping it (that is its "Map this indicator" button), so a reader can compare the
   * programme total for C.2.1 while B.2.1 is painted on the map.
   */
  toggleDockInd: (id: string) => void;
  /** §F5.9 ✕ / an explicit open (`null` closes). */
  setDockInd: (id: string | null) => void;

  /* ------------------------------------------ M3 §F5.10/§G6 AOI clip & swipe */

  /**
   * §F5.10 draw-AOI: the finished (or in-progress ≥3 vertices) ring plus its §G7 area.
   *
   * `null` **releases the clip**, because a clip with no polygon is a mask over nothing —
   * §F5.10's "re-click clears + releases clip" in one action, so no caller can leave
   * `aoiClip` true with `aoi` null and blank the thematic panes.
   *
   * **The caller does not supply `id`** — `setAoi` computes it from the ring (M4), so the
   * identity every §H2/§H3 request and the §F6 dock key on is a *function* of the polygon
   * rather than a second field two writers could set differently.
   */
  setAoi: (aoi: { pts: [number, number][]; areaKm2: number } | null) => void;
  /**
   * §G6 "mutually exclusive": turning the clip on turns swipe off. Silent — §G6 wants a
   * toast *explaining* the pause, and a store action has no `t()`; the caller reads the
   * previous `swipe` and raises the message. The invariant lives here so the two can never
   * both own `clip-path` on `rasterPane`/`choroPane`.
   *
   * Refuses to switch on with no `aoi` (see `setAoi`).
   *
   * There is deliberately no `toggleAoiClip`/`toggleSwipe`: both callers have to read the
   * previous value anyway, to decide which §G6 toast to raise, so a flip-only action would
   * be a second way to reach the same state that no one can actually use.
   */
  setAoiClip: (on: boolean) => void;
  /** §G6 swipe; the mirror of `setAoiClip` — switching it on releases the AOI clip. */
  setSwipe: (on: boolean) => void;
  /** §F5.10/§H2: which AOI panel the analysis dock has focused (M4). */
  setAoiFocus: (id: string | null) => void;

  /* --------------------------------------- M4 §F5.6(e)/§F6 analysis result layers */

  /**
   * Register one entry of a finished run's `result_layers` (§F6 "optional result layers on
   * map (registered in legend block (e))").
   *
   * **Idempotent on `spec.id`**, which every tool builds as `{tool}:{job_id}`: a reader who
   * re-opens the same result — or a §E7 cache hit that answers the same job twice — gets
   * the layer refreshed in place rather than a second copy of it stacked on the first.
   * Replacing in place also keeps the list in the order the runs happened, which is the
   * order the legend prints.
   */
  addResultLayer: (spec: ResultLayerSpec, meta: ResultLayerMeta) => void;
  /** §F5.6(e)'s per-row ✕ — one result off the map. */
  removeResultLayer: (id: string) => void;
  /** §F5.6(e)'s block ✕ and §F6's "Clear result layers (n)" — the same action, one list. */
  clearResultLayers: () => void;
}

/** §F5.2 *Map mode* Seg — "[Single indicator | Bivariate (two)]". */
export type MapMode = 'single' | 'bivariate';

/** §F5.5 "Max 2 overlays (toast)" — fill + graduated circles + graduated rings. */
export const MAX_OVERLAYS = 2;

/**
 * §F5.2 default per-layer opacity — the mockup's `fillOpacity: .58` for a thematic
 * raster. A first-click layer has no entry in `layerOpacity`, so every consumer reads
 * `layerOpacity[id] ?? DEFAULT_LAYER_OPACITY` rather than seeding the map on toggle.
 */
export const DEFAULT_LAYER_OPACITY = 0.58;

export type AppStore = AppState & AppActions;

/** §D1 `/meta/config.years` — mirrored client-side so boot does not block on the API. */
export const YEARS = [2021, 2022, 2023, 2024, 2025, 2026];

/** Last of `YEARS`; the value `selectYear` falls back to if `yearIdx` is ever out of range. */
export const FALLBACK_YEAR = 2026;

/**
 * The indicator a cold load maps — §F5.2's list is long and an empty map is not a landing
 * page. B.2.1 is the programme's headline contractual indicator and the one MELS reports
 * live at every stratum, so it is the map that best answers "what is this portal".
 *
 * A permalink still wins: `hydrateFromHash` runs before React and overwrites `indicator`,
 * and `PermalinkSync` drops an id the catalogue does not confirm — which then falls back
 * here rather than to a blank map.
 */
export const DEFAULT_INDICATOR = 'b21';

/**
 * `years[yearIdx]` as one selector — the year every §F1 `['values', …]` query key uses.
 * `setYearIdx` clamps, so the fallback is unreachable outside a hand-written setState.
 */
export const selectYear = (s: Pick<AppState, 'years' | 'yearIdx'>): number =>
  s.years[s.yearIdx] ?? FALLBACK_YEAR;

export const COUNTRY_NAMES: Record<Country, string> = {
  ALL: 'All countries',
  BFA: 'Burkina Faso',
  MLI: 'Mali',
  NER: 'Niger',
  NGA: 'Nigeria',
};

export const ROLE_NAMES: Record<Role, string> = {
  public: 'Public visitor',
  field: 'Field & extension',
  partner: 'Partner institution',
  analyst: 'MEL analyst',
  manager: 'Manager / Admin',
};

/**
 * §F4: the splash opens on the **first load of a browser session** and not again, unless
 * the reader asks for it with the "Overview" nav button.
 *
 * `sessionStorage`, not `localStorage`: spec §F4 calls the suppression optional and scopes
 * it to the session, and that is the right scope — a reader who comes back tomorrow is
 * arriving fresh and the landing page is the thing that tells them what this portal is. A
 * `localStorage` flag would show it once, ever, to each browser.
 *
 * Reading it can throw: Safari's private mode and a locked-down enterprise profile both
 * make `sessionStorage` a property access that raises rather than a null. A splash is not
 * worth a white screen, so a failure means "show it", which is the same answer as a first
 * visit.
 */
export const SPLASH_SEEN_KEY = 'sv.splashSeen';

/**
 * The client review's R1 sign-in gate, suppressed by the same rule and for the same reason.
 *
 * A **second** key, not a reuse of `SPLASH_SEEN_KEY`: the two surfaces are dismissed
 * separately (the gate always first) and are reopened by different things — the topbar's
 * "Overview" button raises the splash again, and nothing raises the gate again inside a
 * session. One shared flag would mean closing the gate also marked the splash seen, i.e.
 * the landing page nobody had been shown yet would never appear.
 */
export const GATE_SEEN_KEY = 'sv.gateSeen';

/**
 * Reading `sessionStorage` can throw: Safari's private mode and a locked-down enterprise
 * profile both make it a property access that raises rather than a null. Neither a splash
 * nor a gate is worth a white screen, so a failure means "show it", which is the same
 * answer as a first visit; a failed *write* just means it opens again next load.
 */
function sessionFlag(key: string): boolean {
  try {
    return globalThis.sessionStorage?.getItem(key) === '1';
  } catch {
    return false;
  }
}

function markSessionFlag(key: string): void {
  try {
    globalThis.sessionStorage?.setItem(key, '1');
  } catch {
    /* see `sessionFlag` */
  }
}

export function splashSuppressed(): boolean {
  return sessionFlag(SPLASH_SEEN_KEY);
}

export function suppressSplash(): void {
  markSessionFlag(SPLASH_SEEN_KEY);
}

export function gateSuppressed(): boolean {
  return sessionFlag(GATE_SEEN_KEY);
}

export function suppressGate(): void {
  markSessionFlag(GATE_SEEN_KEY);
}

/**
 * Defaults per §F1 + the mockup's initial `state`.
 *
 * `splashOpen` is **false here and true in the store**: this object is the pure,
 * environment-free default that `shareHash`/`applyHash` round-trip against and that
 * `state.test.ts` asserts field by field, and making it read `sessionStorage` would make a
 * permalink test depend on which test ran before it. The session-derived value belongs to
 * the *store*, which is the thing with a lifetime — see `useAppStore`.
 */
export const initialState: AppState = {
  role: 'public',
  user: null,
  country: 'ALL',
  scope: null,
  reportingLevel: 1,
  lastAdminLevel: 1,
  indicator: DEFAULT_INDICATOR,
  overlays: [],
  biv: { on: false, y: null },
  years: YEARS,
  yearIdx: YEARS.length - 1,
  // Nothing has chosen this year: it is the last of the programme window, and the
  // catalogue's `default_year` replaces it the moment `/indicators` answers.
  yearPinned: false,
  // Annual by default: the year on the slider means that year. An accumulating default
  // would make the map answer a question the reader did not ask (see `AppState`).
  cumulative: false,
  layersOn: new Set<string>(),
  layerOpacity: {},
  indStyle: { ramp: null, method: 'quantile', k: 5, opacity: 0.78 },
  legendMode: 'values',
  domFilter: '',
  layerQuery: '',
  // §F5.1's four basemaps are unchanged and so are their keys; this is which one a cold
  // boot starts on. **OSM standard, not Carto light.** The programme's readers work at
  // commune and watershed scale, where the question is usually "which village, which road,
  // which river is this" — and light_all is a deliberately faint canvas that answers none
  // of them without a thematic layer on top. OSM carries the place names, roads and
  // waterways at the zooms this map is actually used at.
  //
  // Nothing about sharing changes: `serialize` always emits `bm=` (permalink.ts), so every
  // link ever written names its basemap explicitly and still restores exactly that one.
  // Only a URL with no `bm=` at all — a bare `/map`, a hand-written hash — lands here.
  basemap: 'streets',
  aoi: null,
  aoiClip: false,
  aoiFocus: null,
  results: [],
  swipe: false,
  selectedUnit: null,
  dockInd: null,
  analysisOpen: false,
  splashOpen: false,
  gateOpen: false,
  tourStep: -1,
  sidebarOpen: true,
};

/**
 * Where `'watershed'` goes when §M3.5 takes the radio away — the mapped indicator turned
 * out to have no native watershed values.
 *
 * Unscoped that is `lastAdminLevel`, as it always was. **Under a watershed scope it cannot
 * be**: the subtree holds no admin units at all, so demoting to level 1 or 2 would ask §D3
 * for regions inside a watershed and paint nothing. Partnerships are the honest answer
 * there — §E4 area-weights them from the same admin cells the watershed estimate came from,
 * and they are inside the filter the reader set.
 */
function demotedLevel(s: Pick<AppState, 'scope' | 'lastAdminLevel'>): ReportingLevel {
  return levelWithinScope(s.scope, s.lastAdminLevel, PARTNERSHIP_LEVEL);
}

/**
 * E4: a write that can switch the scope mask on takes swipe off with it.
 *
 * The mirror of `setAoiClip`'s `swipe: false`, and needed in four actions rather than one
 * because the mask has no switch of its own: it comes on when a filtered geography and a
 * raster meet, whichever of the two arrived last, and the geography can arrive as a scope
 * (`setScope`) or as the cascade's country rung (`setCountry`). Written as a wrapper so the
 * rule is one expression read against the state the patch is about to produce, rather than
 * four copies of a condition that would drift.
 */
function releasingSwipe(s: AppStore, patch: Partial<AppState>): Partial<AppState> {
  return s.swipe && scopeMaskActive({ ...s, ...patch }) ? { ...patch, swipe: false } : patch;
}

/**
 * "Country selector is global" (§F1): every derived view reads this one field, so
 * changing it also drops the unit selection made under the previous geography.
 */
export const useAppStore = create<AppStore>((set) => ({
  ...initialState,
  // §F4: open on the session's first load. Evaluated once, when the store is created —
  // which is once per page load, i.e. exactly the granularity "first load" means.
  splashOpen: !splashSuppressed(),
  // R1, the same rule one step earlier: the gate is what a cold session actually lands on,
  // and the splash waits behind it (`features/landing/index.tsx`).
  gateOpen: !gateSuppressed(),
  // E4 (round 5): the country rung raises the mask on its own, so this is the fourth write
  // that has to release a running swipe — see `releasingSwipe`. It reads the patch, so the
  // case where the new country *drops* an out-of-country scope is counted correctly: the mask
  // moves from the region's outline to the country's, and stays up.
  setCountry: (country) =>
    set((s) =>
      releasingSwipe(s, {
        country,
        selectedUnit: null,
        // §F5.8: the two geography controls are one geography. Moving the global selector to a
        // country the scope is not in would leave the map filtered to a region of somewhere
        // else — so the narrower filter yields to the broader one. `ALL` contains every scope
        // and clears nothing; a cross-border watershed (`country: null`) cannot be *proved*
        // inside the new country and is dropped, which is the explainable answer.
        // R5: out of a filter is back to the unfiltered level, which is regions.
        ...(s.scope && country !== 'ALL' && s.scope.country !== country
          ? { scope: null, reportingLevel: NO_SCOPE_LEVEL, lastAdminLevel: 1 as const }
          : {}),
      }),
    ),
  setScope: (scope) =>
    set((s) =>
      // E4: filtering with a raster on is what raises the scope mask, so it is one of the
      // three writes that release swipe (`releasingSwipe`).
      releasingSwipe(s, {
        scope,
        ...(scope.country && scope.country !== s.country && scope.country in COUNTRY_NAMES
          ? { country: scope.country as Country }
          : {}),
        reportingLevel: levelWithinScope(scope, s.reportingLevel, defaultScopeLevel(scope)),
        // The previously selected unit is almost certainly outside the new filter, and a
        // profile panel describing a unit the map no longer draws is the §F5.11 case the
        // country selector already handles this way.
        selectedUnit: null,
      }),
    ),
  // Back to regions, not to the level the scope forced: a region scope maps communes, and
  // clearing the filter must not leave the reader on a programme-wide 1 048-polygon commune
  // map they never asked for.
  //
  // Regions rather than `lastAdminLevel` since client review R5 (`NO_SCOPE_LEVEL`): with the
  // radios gone there is no level a reader can have been on *other* than the one the cascade
  // implied, so "the level before the filter" and "the unfiltered level" are one fact. This
  // also fixes the shared-link case the old rule got wrong from the other side — a restored
  // `sc=<region>&lv=2` link had `lastAdminLevel: 2` hydrated straight from the hash, and
  // clearing its filter opened the programme-wide commune map.
  //
  // **D3 adds the country.** The cascade's country box is the first of its five rungs, so
  // "clear the filter" that left `BFA` standing cleared four rungs out of five and the map
  // stayed inside one country — the state IFDC described as "not back to the first level".
  // One condition covers both halves: there is something to clear if either field is set.
  clearScope: () =>
    set((s) =>
      s.scope || s.country !== 'ALL'
        ? {
            scope: null,
            country: 'ALL' as const,
            reportingLevel: NO_SCOPE_LEVEL,
            lastAdminLevel: 1 as const,
          }
        : {},
    ),
  // §F5.7 "Updates live on year/**level** changes" and §F5.8 "Radio change → re-render
  // choropleth/overlays/**unit panel**": a reporting-level change re-aggregates the map,
  // but the selected unit is still the same place and its profile must follow, not vanish.
  // (Only the *country* filter closes the panel — §F5.11.) NOTES #202.
  // `lastAdminLevel` rides along so the §M3.5 fallback has somewhere to go back to; it is
  // written here rather than derived because once `reportingLevel` is `'watershed'` the
  // admin level the reader came from is no longer anywhere in the state.
  setReportingLevel: (reportingLevel) =>
    set(
      isOverlayLevel(reportingLevel)
        ? { reportingLevel }
        : { reportingLevel, lastAdminLevel: reportingLevel },
    ),
  reconcileReportingLevel: (indicatorId, watershedSupported) =>
    set((s) => {
      // Read inside `set`, so the comparison is against the store as it is *now* and not as it
      // was when the caller rendered — that is the whole point (see the interface).
      if (s.indicator !== indicatorId) return {};
      return s.reportingLevel === WATERSHED_LEVEL && !watershedSupported
        ? { reportingLevel: demotedLevel(s) }
        : {};
    }),
  // §M3.5's year twin: `b21` now carries 2025–26 and nothing else, so a reader arriving on
  // the default 2026 is fine but one who drags to 2021 and *then* picks b21 would be left
  // on an empty map. Snapping to the newest year the indicator actually has is the same
  // move `reconcileReportingLevel` makes for the level — and it is deliberately a snap to
  // the *newest*, not the nearest: a programme reader wants the current figure, and a
  // cumulative series' newest year is the one the headline number comes from.
  // The cold-load year. `/indicators` is the only thing that knows how much of a year has
  // actually been reported, so the boot year cannot be a constant: the last programme year
  // is 2026 and B.2.1's 2026 is three rows, which opened the portal on a single painted
  // polygon (NOTES #361's "one thing to say out loud to the client"). Applied once, and
  // never over a year the reader or a permalink has already chosen.
  applyDefaultYear: (year) =>
    set((s) => {
      if (s.yearPinned || year === null || year === undefined) return {};
      const idx = s.years.indexOf(year);
      // Pinned either way: a `default_year` outside this deploy's programme window is still
      // an answer, and re-asking on every catalogue refetch would only churn.
      return idx >= 0 ? { yearIdx: idx, yearPinned: true } : { yearPinned: true };
    }),
  snapYearToData: (available, preferred = null) =>
    set((s) => {
      const current = s.years[s.yearIdx];
      if (!available.length || (current !== undefined && available.includes(current))) {
        return {};
      }
      // The indicator's own default first — the same year a cold load would have opened on
      // — and the newest year with data only when that is not one of them. Snapping to the
      // newest was the old rule and it lands on exactly the near-empty year `default_year`
      // exists to avoid.
      const wanted = preferred !== null && available.includes(preferred) ? preferred : null;
      const idx =
        wanted !== null
          ? s.years.indexOf(wanted)
          : s.years.reduce((best, year, i) => (available.includes(year) ? i : best), -1);
      return idx >= 0 ? { yearIdx: idx } : {};
    }),
  setBasemap: (basemap) => set({ basemap }),
  setRole: (role) => set({ role }),
  setSession: (user) =>
    set({
      role: user.role,
      user:
        user.id === null
          ? null
          : {
              id: user.id,
              name: user.name ?? String(user.id),
              org: user.org,
              country_scope: user.country_scope,
            },
    }),

  setIndicator: (id, hasWatershedValues = false) =>
    set((s) => ({
      indicator: id,
      // §F5.2 "reset indStyle.ramp" — a fresh auto-symbology (§G2) per indicator.
      indStyle: { ...s.indStyle, ramp: null },
      // A no-longer-mapped indicator must not leave a stale profile behind (§F5.7).
      ...(id === null ? { selectedUnit: null } : {}),
      // §M3.5: the watershed radio is per-indicator, so the *level* is too. Clearing the
      // indicator, or picking one MELS does not report per watershed, drops the map back to
      // the admin level it came from — otherwise the widget would render with no radio
      // checked and the choropleth would ask for a stratum the indicator has no values in.
      ...(s.reportingLevel === WATERSHED_LEVEL && !(id && hasWatershedValues)
        ? { reportingLevel: demotedLevel(s) }
        : {}),
    })),
  setIndStyle: (patch) => set((s) => ({ indStyle: { ...s.indStyle, ...patch } })),
  setYearIdx: (idx) =>
    set((s) => ({
      yearIdx: Math.min(Math.max(0, Math.trunc(idx)), s.years.length - 1),
      // The reader has chosen a year; a catalogue landing a moment later must not take it.
      yearPinned: true,
    })),
  setCumulative: (cumulative) => set({ cumulative }),
  setLegendMode: (legendMode) => set({ legendMode }),
  setDomFilter: (domFilter) => set({ domFilter }),
  setSelectedUnit: (selectedUnit) => set({ selectedUnit }),
  setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
  // E2. `indStyle.ramp` is reset with the indicator for the reason §F5.2 gives on
  // `setIndicator`: a ramp chosen for one indicator must not survive onto the next one the
  // reader maps. The empty-return branches keep this a no-op when the other side is already
  // empty, so a reader clicking between tabs on a bare map re-renders nothing.
  switchSidebarTab: (tab) =>
    set((s) => {
      if (tab === 'layers') {
        if (!selectIndicatorMapped(s)) return {};
        return {
          indicator: null,
          indStyle: { ...s.indStyle, ramp: null },
          overlays: [],
          biv: { on: false, y: null },
          // §F5.7: a profile panel outliving the indicator it described is the same stale
          // panel `setIndicator(null)` closes.
          selectedUnit: null,
        };
      }
      return selectLayersMapped(s) ? { layersOn: new Set<string>() } : {};
    }),
  setAnalysisOpen: (analysisOpen) =>
    // §F6 opens over the map, and the mockup's own handler hides the info panel when it
    // does. The unit profile is this portal's `#infopanel`: 390 px of dock next to 360 px
    // of profile leaves no map between them.
    set(analysisOpen ? { analysisOpen, selectedUnit: null } : { analysisOpen }),
  setSplashOpen: (splashOpen) => {
    if (!splashOpen) suppressSplash();
    set({ splashOpen });
  },
  setGateOpen: (gateOpen) => {
    if (!gateOpen) suppressGate();
    set({ gateOpen });
  },

  toggleLayer: (id, on) =>
    set((s) => {
      const next = new Set(s.layersOn);
      const wanted = on ?? !next.has(id);
      // R4: a second raster is refused, not swapped in. Swapping would make the checkbox a
      // radio without looking like one — the reader would click "add" and watch a different
      // layer disappear. The row is disabled instead, and this is its backstop.
      if (wanted && isRasterLayerId(id)) {
        const active = activeRasterOf(s.layersOn);
        if (active !== null && active !== id) return {};
      }
      if (wanted) next.add(id);
      else next.delete(id);
      return releasingSwipe(s, { layersOn: next });
    }),
  // D2's raster select, as one atomic write. `RASTER_IDS` is the same register
  // `toggleLayer` and `activeRasterOf` read, so a catalogue that has not landed yet means
  // "nothing is a raster": the pick is simply added, and `registerRasterLayers` normalises
  // the set the moment the rule becomes evaluable — the permalink path, unchanged.
  setActiveRaster: (id, rasterIds) =>
    set((s) => {
      const rasters = rasterIds ? new Set(rasterIds) : RASTER_IDS;
      const next = new Set<string>();
      for (const on of s.layersOn) if (!rasters.has(on)) next.add(on);
      if (id) next.add(id);
      return releasingSwipe(s, { layersOn: next });
    }),
  // Clamped: the §F5.2 slider is 10–100 %, and a layer at 0 is indistinguishable from a
  // layer that failed to load — the reader would blame the tiles, not the control.
  setLayerOpacity: (id, opacity) =>
    set((s) => ({
      layerOpacity: { ...s.layerOpacity, [id]: Math.min(1, Math.max(0.1, opacity)) },
    })),
  setLayerQuery: (layerQuery) => set({ layerQuery }),

  addOverlay: (id) =>
    set((s) =>
      s.overlays.some((o) => o.id === id) || s.overlays.length >= MAX_OVERLAYS
        ? {}
        : { overlays: [...s.overlays, { id }] },
    ),
  removeOverlay: (id) => set((s) => ({ overlays: s.overlays.filter((o) => o.id !== id) })),
  // A cleared override drops the `color` key entirely rather than storing `undefined`:
  // §G4 reads `if (overlays[i].color)`, and the permalink (Task 8) serialises the array.
  setOverlayColor: (id, color) =>
    set((s) => ({
      overlays: s.overlays.map((o) => (o.id === id ? (color ? { id: o.id, color } : { id: o.id }) : o)),
    })),
  clearOverlays: () => set((s) => (s.overlays.length ? { overlays: [] } : {})),

  setMapMode: (mode) =>
    set((s) => {
      if (mode === 'bivariate') {
        // Re-selecting the mode already in force must not throw away a chosen Y — the
        // §F5.2 Seg fires on every click, not only on a change.
        if (s.biv.on) return {};
        return { biv: { on: true, y: null }, overlays: [] };
      }
      return s.biv.on ? { biv: { on: false, y: null } } : {};
    }),
  setBivY: (id) => set((s) => (s.biv.on ? { biv: { on: true, y: id } } : {})),

  toggleDockInd: (id) => set((s) => ({ dockInd: s.dockInd === id ? null : id })),
  setDockInd: (dockInd) => set({ dockInd }),

  // The id is derived here and nowhere else, so `aoi.id` is by construction the identity of
  // `aoi.pts` (M4, `lib/aoi.ts`). The permalink is the one other writer of this field and it
  // calls `ringId` itself, because it writes with `setState` rather than the actions.
  setAoi: (aoi) =>
    set(
      aoi
        ? { aoi: { pts: aoi.pts, areaKm2: aoi.areaKm2, id: ringId(aoi.pts) } }
        : { aoi: null, aoiClip: false, aoiFocus: null },
    ),
  setAoiClip: (on) =>
    set((s) => {
      if (!on) return s.aoiClip ? { aoiClip: false } : {};
      if (!s.aoi) return {}; // nothing to clip to — §F5.10 draws the AOI first
      return { aoiClip: true, swipe: false };
    }),
  // E4: refused while the scope mask would hold the raster pane — the §G6 exclusion, extended
  // to the third writer of `clip-path` on those panes. Asked of the state this write would
  // produce, not of the one it starts from (`swipeBlockedByScope`), because the write releases
  // the AOI clip and the AOI clip is what keeps the mask down. Silent here and explained by
  // the caller, like the AOI pair above; `MapTools` asks the same question and toasts.
  setSwipe: (on) =>
    set((s) => {
      if (!on) return s.swipe ? { swipe: false } : {};
      if (swipeBlockedByScope(s)) return {};
      return { swipe: true, aoiClip: false };
    }),
  setAoiFocus: (aoiFocus) => set({ aoiFocus }),

  addResultLayer: (spec, meta) =>
    set((s) => {
      const entry: ResultLayer = {
        id: spec.id,
        jobId: meta.jobId,
        tool: meta.tool,
        label: meta.label ?? spec.label,
        spec,
        createdAt: Date.now(),
      };
      const at = s.results.findIndex((r) => r.id === entry.id);
      if (at === -1) return { results: [...s.results, entry] };
      const results = [...s.results];
      results[at] = entry;
      return { results };
    }),
  removeResultLayer: (id) =>
    set((s) =>
      s.results.some((r) => r.id === id) ? { results: s.results.filter((r) => r.id !== id) } : {},
    ),
  clearResultLayers: () => set((s) => (s.results.length ? { results: [] } : {})),
}));

/** Test/helper hook: restore the documented defaults (fresh Set/objects each time). */
export function resetAppStore(): void {
  // The R4 register is catalogue-derived, so a test that seeded it must not leak into the
  // next one; whoever holds the `['layers']` query re-registers on the next arrival.
  registerRasterLayers([]);
  useAppStore.setState({
    ...initialState,
    scope: null,
    layersOn: new Set<string>(),
    layerOpacity: {},
    overlays: [],
    results: [],
    biv: { ...initialState.biv },
    years: [...YEARS],
    indStyle: { ...initialState.indStyle },
  });
}
